import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { processSteps } from '../../../mocks/home';

const ProcessSection = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-white" id="process">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-0.5 w-8 bg-teal"></div>
            <span className="text-teal font-body text-xs tracking-widest uppercase font-medium">How It Works</span>
            <div className="h-0.5 w-8 bg-teal"></div>
          </div>
          <h2 className="font-heading font-800 text-navy text-4xl lg:text-5xl mb-4">
            Your Move, Simplified
          </h2>
          <p className="text-charcoal/60 font-body text-lg max-w-xl mx-auto leading-relaxed">
            We&apos;ve streamlined every step so your moving day is smooth, predictable, and stress-free from start to finish.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connector Line Desktop */}
          <div className="hidden lg:block absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-teal/0 via-teal/30 to-teal/0"></div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {processSteps.map((step, i) => (
              <div
                key={step.step}
                className={`relative flex flex-col items-center text-center transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: visible ? `${i * 120}ms` : '0ms' }}
              >
                {/* Step Number Circle */}
                <div className="relative mb-6">
                  <div className="w-24 h-24 flex items-center justify-center bg-navy rounded-2xl border-2 border-teal/30 relative z-10">
                    <i className={`${step.icon} text-teal text-3xl`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center bg-teal rounded-lg z-20">
                    <span className="font-heading font-800 text-navy text-xs">{step.step}</span>
                  </div>
                </div>

                <h3 className="font-heading font-700 text-navy text-base mb-2">{step.title}</h3>
                <p className="text-charcoal/60 font-body text-sm leading-relaxed">{step.description}</p>

                {/* Arrow between steps (mobile/tablet) */}
                {i < processSteps.length - 1 && (
                  <div className="lg:hidden mt-4 text-teal/40">
                    <i className="ri-arrow-down-line text-xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className={`mt-16 bg-navy rounded-3xl p-10 flex flex-col lg:flex-row items-center justify-between gap-6 transition-all duration-700 delay-600 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <h3 className="font-heading font-700 text-white text-2xl mb-2">Ready to start your move?</h3>
            <p className="text-text-muted font-body text-sm">Get your free, transparent quote in minutes. No obligation.</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Link
              to="/#quote"
              className="bg-teal hover:bg-teal-dark text-navy font-heading font-700 px-7 py-3.5 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              Get Free Quote
            </Link>
            <a
              href="tel:+16123674481"
              className="bg-white/10 hover:bg-white/20 text-white font-heading font-600 px-7 py-3.5 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-sm border border-white/20"
            >
              Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
